import { useEffect } from 'react'

import { useWebsocketContext } from '@/modules/websocketContext'
import type { SseEvent } from '../types'
import { useChatMutations, useCurrentSessionId } from './chat'

// After a reconnect, wait this long for events to resume before assuming the
// turn finished during the drop. A genuinely live turn keeps delivering
// frames (which cancel the pending reconcile); a turn whose tail was lost
// stays silent, so we re-fetch to recover the answer.
//
// CONTRACT: this must be strictly greater than daystrom's `agent_status`
// heartbeat interval. The heartbeat is what keeps a live-but-quiet turn (e.g. a
// silent subagent run) delivering frames; if it ticks slower than this delay, a
// reconnect mid-turn would reconcile a still-running turn as if it had finished.
const RECONNECT_RECONCILE_DELAY_MS = 2500

// Subscribes the open chat session to its ChatChannel stream and feeds each
// event into the store. The turn itself runs server-side (lukita's
// ChatTurnJob); this is purely the delivery path. Mounted by the chat
// container while a session is open, and torn down / re-established when the
// session or consumer changes. `subscribe` is memoized on the consumer, so
// listing it in the deps is what keeps delivery single: a token refresh swaps
// the consumer, `subscribe`'s identity changes, this effect drops the old
// subscription and creates exactly one on the new connection. Without that
// dep the chat stays bound to the discarded consumer and goes silent.
export const useChatChannel = (slug: string) => {
  const sessionId = useCurrentSessionId()
  const { isReady, subscribe, unsubscribe } = useWebsocketContext()
  const { ingestStreamEvent, reconcileInFlightTurn } = useChatMutations()

  useEffect(() => {
    if (!isReady || !sessionId) return

    let reconcileTimer: ReturnType<typeof setTimeout> | null = null
    const cancelReconcile = () => {
      if (reconcileTimer !== null) {
        clearTimeout(reconcileTimer)
        reconcileTimer = null
      }
    }

    const subscription = subscribe(
      { channel: 'ChatChannel', chat_session_id: sessionId },
      {
        connected: (reconnected: boolean) => {
          // Only a true reconnect (not the first connect) can have missed a
          // turn's tail. Schedule a reconcile; if events resume first the
          // `received` handler cancels it (the turn is alive). If the channel
          // stays quiet, the turn finished during the drop and we re-fetch.
          if (!reconnected) return
          cancelReconcile()
          reconcileTimer = setTimeout(() => {
            reconcileTimer = null
            void reconcileInFlightTurn(slug, sessionId)
          }, RECONNECT_RECONCILE_DELAY_MS)
        },
        received: (data: unknown) => {
          // Any inbound frame means the turn is alive and streaming, so a
          // pending post-reconnect reconcile would be premature.
          cancelReconcile()
          // Channel rejections / control frames arrive without a string
          // `name`; only forward genuine chat events.
          const name = (data as { name?: unknown })?.name
          if (typeof name !== 'string') return
          ingestStreamEvent(slug, sessionId, data as SseEvent)
        },
      },
    )
    return () => {
      cancelReconcile()
      unsubscribe(subscription)
    }
  }, [
    isReady,
    sessionId,
    slug,
    subscribe,
    unsubscribe,
    ingestStreamEvent,
    reconcileInFlightTurn,
  ])
}
